import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  RefreshControl,
  View,
  useWindowDimensions,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { FlashList } from '@shopify/flash-list';
import type { ListRenderItem } from '@shopify/flash-list';
import { useTranslation } from 'react-i18next';
import { router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { useQuery, useQueryClient, useInfiniteQuery, InfiniteData } from '@tanstack/react-query';
import { BlurTargetView } from 'expo-blur';
import {
  PageParam,
  getNextPageSkipOrCursor,
  buildPaginationParams,
} from '@/hooks/usePaginationUtils';
import { useIsLargeScreen } from '~/hooks/useIsLargeScreen';
import { useProfileAnimations } from './useProfileAnimations';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { Box } from '~/components/gluestack/box';
import { Text } from '~/components/gluestack/text';
import { VStack } from '~/components/gluestack/vstack';
import { HStack } from '~/components/gluestack/hstack';
import { MessageView } from '~/components/ui/MessageView';
import { ErrorInListView } from '~/components/ui/ErrorInListView';
import {
  Alert02Icon,
  FolderLockedIcon,
  AlbumNotFound02Icon,
  QuillWrite02Icon,
  Add01Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { Image } from 'expo-image';
import { SafeBlurView as BlurView } from '~/components/ui/SafeBlurView';
import { useTheme } from '~/components/providers/ThemeProvider';
import { ImageViewerTrigger } from '~/components/ui/image-viewer/ImageViewerTrigger';
import { useImageViewerState } from '~/components/ui/image-viewer/ImageViewerProvider';
import { ANDROID_CSS_BLUR_CLIP_STYLE, type IExpoImageBlurConfig } from '@/utils/expoImageBlur';
import { BlurredExpoImage } from '@/components/ui/BlurredExpoImage';
import { ProfileMobileHeader, useProfileMobileHeaderHeight } from './ProfileMobileHeader';
import { InlineTabsBar } from '~/components/ui/InlineTabsBar';
import type { TabItem } from '~/components/ui/InlineTabsBar';
import { CountBadge } from '~/components/ui/CountAndMyTypeBadge';
import { ProfileContextMenu } from './ProfileContextMenu';
import { ProfileActions } from './ProfileActions';
import { ProfileOverview } from './ProfileOverview';
import { ProfileTabAbout } from './ProfileTabAbout';
import { ProfileTabAboutEvent } from './ProfileTabAboutEvent';
import { ProfileTabEvents } from '@/components/domain/profile/ProfileTabEvents';
import { ProfileTabWeight } from './ProfileTabWeight';
import { MembersService } from '@/services/domain/MembersService';
import { PostsView, QuotaTypes, Post } from '@/models/Post';
import { PhotosView, Photo } from '@/models/Photo';
import { CardWrapper } from '@/components/domain/posts/CardWrapper';
import { PostCard } from '@/components/domain/posts/PostCard';
import { PostCardUpgrade } from '@/components/domain/posts/PostCardUpgrade';
import { PhotoCard } from '@/components/domain/photos/PhotoCard';
import SafetySettingsContent from '@/components/domain/common/SafetySettingsContent';
import { SensitivePhotosReminderCard } from '@/components/domain/photos/SensitivePhotosReminderCard';
import { ProfilePicture } from '~/components/domain/common/ProfilePicture';
import { useAppState } from '@/hooks/useAppState';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useQueryCacheUtils, useRemovePostFromCache } from '@/hooks/useQueryCacheUtils';
import { useMemberPreviewPhotos, buildProfilePhotoCarousel } from '@/hooks/useMemberPreviewPhotos';
import { useFlashListOptimizer } from '@/hooks/useFlashListOptimizer';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { useAuth } from '~/components/providers/AuthProvider';
import { MemberDetails } from '~/models/MemberDetails';
import { Fab } from '~/components/gluestack/fab';
import { HugeIconsWrapper } from '~/components/ui/HugeIconsWrapper';
import { ContextSheet } from '@/components/ui/ContextSheet';
import { AddPost } from '@/components/domain/posts/AddPost';
import { ImageUploadButton } from '@/components/ui/image/ImageUploadButton';
import { PhotosService } from '@/services/domain/PhotosService';
import { WebDropImageUpload } from '@/components/ui/WebDropImageUpload';
import { plainToClass } from '@/models/transform';
import { PremiumUpgradeCard } from '@/components/domain/common/PremiumUpgradeCard';
import { useCommunity } from '~/hooks/useCommunity';
import { EventOverview } from '@/components/domain/events/EventOverview';
import { EventActions } from '@/components/domain/events/EventActions';
import { EventContextMenu } from '@/components/domain/events/EventContextMenu';
import { EventsService } from '@/services/domain/EventsService';
import { useSensitivePhotosReminder } from '@/hooks/useSensitivePhotosReminder';

const CONTENT_HEADER_OVERLAP = 20;
const STICKY_TABS_SCROLL_NUDGE = 4;
const TABS_BAR_HEIGHT = 54;
/** Approx. vertical gap between the main sticky tabs and the Public/Private row in the scroll content. */
const PHOTOS_FOLDER_TABS_UNDER_MAIN_GAP = 8;
const EMPTY_PHOTOS: Photo[] = [];

/**
 * Maximum blur strengths reached at full scroll on the profile header.
 *
 * Applied to a static `BlurredExpoImage` stacked under the sharp header image;
 * the visible blur ramps in via animated `opacity` (cross-platform best practice).
 * Per-frame blur animation (`filter: blur(Npx)` / `blurRadius`) is intentionally
 * avoided here — it re-rasterizes on every scroll frame and was the dominant ANR
 * source on Android 12+ (GROKIO-APP-MC, MD, N0, MP, VF on 6.0.194).
 */
const PROFILE_SCROLL_BLUR_CONFIG: IExpoImageBlurConfig = {
  iosBlurRadius: 80,
  webBlurRadius: 20,
  androidCssBlurPx: 20,
};

export type ProfileTab = 'about' | 'posts' | 'photos' | 'events' | 'weight';

interface ProfileScreenProps {
  userName: string;
  isEvent?: boolean;
}

type ProfileTabKind = 'infiniteQuery' | 'component';
type ProfileTabQueryType = 'posts' | 'photos';

type ProfileTabComponentProps = {
  member: MemberDetails;
};

type ProfileTabComponent = React.ComponentType<ProfileTabComponentProps>;

type ProfileTabConfig = TabItem & {
  kind: ProfileTabKind;
  queryType?: ProfileTabQueryType;
  component?: ProfileTabComponent;
};

type ListItem =
  | { type: 'parallaxImageSpacer' }
  | { type: 'contentHeader' }
  | { type: 'tabs' }
  | { type: 'spacer'; className?: string }
  | { type: 'postsLoading' }
  | { type: 'listError' }
  | { type: 'postsEmpty' }
  | { type: 'post'; post: Post; index: number }
  | { type: 'postUpgrade'; index: number }
  | { type: 'photosFolderTabs' }
  | { type: 'sensitivePhotosReminder' }
  | { type: 'photosLoading' }
  | { type: 'photosEmpty' }
  | { type: 'photosLocked' }
  | { type: 'photoRow'; photos: Photo[] }
  | { type: 'photosUpgrade' }
  | { type: 'photosExtraInfo' }
  | { type: 'tabComponent' };

export function ProfileScreen({ userName, isEvent }: ProfileScreenProps) {
  const { t } = useTranslation();
  const isLargeScreen = useIsLargeScreen();
  const insets = useSafeAreaInsets();
  const largeHeaderBlurTargetRef = useRef<View | null>(null);
  const { accountStatus } = useAuth();
  const queryClient = useQueryClient();
  const { width, height } = useWindowDimensions();
  const isWideHeader = width >= 1100;
  const community = useCommunity();
  const { effectiveColorScheme } = useTheme();
  const hasAdvancedWeightFeatures = community?.hasAdvancedWeightFeatures ?? false;
  const photosForCarouselRef = useRef<Photo[]>(EMPTY_PHOTOS);

  const { tab: tabParam } = useLocalSearchParams<{ tab?: string }>();
  const validTabs: ProfileTab[] = ['about', 'posts', 'photos', 'events', 'weight'];
  const initialTab: ProfileTab =
    tabParam && validTabs.includes(tabParam as ProfileTab) ? (tabParam as ProfileTab) : 'about';
  const [activeTab, setActiveTab] = useState<ProfileTab>(initialTab);
  const [isAddPostSheetOpen, setIsAddPostSheetOpen] = useState(false);
  const [isSafetySheetOpen, setIsSafetySheetOpen] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  // Tracks the large-screen cover image load so the loading spinner stays up
  // until the header is ready — mirrors the mobile `imageHeight` gate, which is
  // parallax-only and always 0 on large screens.
  const [largeHeaderImageLoaded, setLargeHeaderImageLoaded] = useState(false);
  const [pendingCaption, setPendingCaption] = useState<string>('');
  const {
    isDismissed: isSensitiveReminderDismissed,
    isLoaded: isSensitiveReminderLoaded,
    dismiss: dismissSensitiveReminder,
  } = useSensitivePhotosReminder();

  const queryKey = isEvent ? ['event-profile', userName] : ['member-details', userName];

  const {
    data: member,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery<MemberDetails>({
    queryKey,
    queryFn: async () => {
      return isEvent
        ? await EventsService.getEventDetails(userName)
        : await MembersService.getMemberDetailsByUsername(userName);
    },
    enabled: !!userName,
    retry: false,
    refetchOnMount: true,
  });

  const isSelf = member?.Id === accountStatus?.MemberId;
  const eventDetails = member?.EventDetails;
  const isEventCreator = isEvent && !!eventDetails?.CanEdit;

  const profileTabs: ProfileTabConfig[] = useMemo(
    () =>
      isEvent
        ? [
            {
              key: 'about',
              label: t('profile.tabs.about', 'About'),
              kind: 'component' as ProfileTabKind,
              component: ({ member }) => (
                <ProfileTabAboutEvent
                  member={member}
                  onPhotosTabPress={() => handleTabChangeRef.current('photos')}
                />
              ),
            },
            {
              key: 'posts',
              label: t('profile.tabs.posts', 'Posts'),
              kind: 'infiniteQuery' as ProfileTabKind,
              queryType: 'posts' as ProfileTabQueryType,
            },
            {
              key: 'photos',
              label: t('profile.tabs.photos', 'Photos'),
              kind: 'infiniteQuery' as ProfileTabKind,
              queryType: 'photos' as ProfileTabQueryType,
            },
          ]
        : [
            {
              key: 'about',
              label: t('profile.tabs.about', 'About'),
              kind: 'component',
              component: ({ member }) => (
                <ProfileTabAbout
                  member={member}
                  onWeightChartPress={
                    hasAdvancedWeightFeatures && (isSelf || member?.HasWeightRecords)
                      ? () => handleTabChangeRef.current('weight')
                      : undefined
                  }
                  onPhotosTabPress={() => handleTabChangeRef.current('photos')}
                />
              ),
            },
            {
              key: 'posts',
              label: t('profile.tabs.posts', 'Posts'),
              kind: 'infiniteQuery',
              queryType: 'posts',
            },
            {
              key: 'photos',
              label: t('profile.tabs.photos', 'Photos'),
              kind: 'infiniteQuery',
              queryType: 'photos',
            },
            ...((member?.EventsCount ?? 0) > 0
              ? [
                  {
                    key: 'events',
                    label: t('profile.tabs.events', 'Events'),
                    kind: 'component' as ProfileTabKind,
                    component: ({ member }: { member: MemberDetails }) => (
                      <ProfileTabEvents member={member} />
                    ),
                  },
                ]
              : []),
            ...(hasAdvancedWeightFeatures && (isSelf || member?.HasWeightRecords)
              ? [
                  {
                    key: 'weight',
                    label: t('profile.tabs.weight', 'Weight'),
                    kind: 'component' as ProfileTabKind,
                    component: ({ member }: { member: MemberDetails }) => (
                      <ProfileTabWeight member={member} isSelf={isSelf} />
                    ),
                  },
                ]
              : []),
          ],
    [t, isEvent, hasAdvancedWeightFeatures, isSelf, member?.HasWeightRecords, member?.EventsCount]
  );

  useEffect(() => {
    const tabKeys = profileTabs.map((t) => t.key);
    if (tabKeys.length > 0 && !tabKeys.includes(activeTab)) {
      setActiveTab('about');
      router.setParams({ tab: undefined });
    }
  }, [profileTabs, activeTab]);

  const insetsTop = insets.top === 0 ? 12 : insets.top;
  const headerHeight = useProfileMobileHeaderHeight(insetsTop, isLargeScreen);

  const [activeFolder, setActiveFolder] = useState<'public' | 'private'>('public');
  const [isFooterSpacerActive, setIsFooterSpacerActive] = useState(false);
  const [contentHeaderHeight, setContentHeaderHeight] = useState(0);
  const currentScrollY = useSharedValue(0);
  const footerSpacerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentHeaderContainerRef = useRef<View | null>(null);
  const handleTabChangeRef = useRef<(tab: ProfileTab) => void>(() => {});
  const { showSuccessToast, showErrorToast, showLoadingToast } = useToastMessages();
  const { updateSingleQuery } = useQueryCacheUtils();
  const handlePostDeleted = useRemovePostFromCache();

  const {
    imageHeight,
    updateScrollY,
    backgroundImageStyle,
    blurredImageOpacityStyle,
    darkenOverlayStyle,
    fixedHeaderStyle,
    contentBackgroundStyle,
  } = useProfileAnimations({
    imageUrl: member?.PhotoUrl,
    tabsLayoutY: 0,
    headerHeight,
  });

  useEffect(() => {
    setContentHeaderHeight(0);
  }, [member?.Id]);

  const [flashListHeight, setFlashListHeight] = useState<number>(height);
  const flashListHeightRef = useRef(height);
  const handleFlashListLayout = useCallback(
    (e: { nativeEvent: { layout: { height: number } } }) => {
      const newHeight = e.nativeEvent.layout.height;
      if (Math.abs(flashListHeightRef.current - newHeight) > 20) {
        flashListHeightRef.current = newHeight;
        setFlashListHeight(newHeight);
      }
    },
    []
  );

  const listRef = useRef<React.ComponentRef<typeof FlashList<any>>>(null);
  const AnimatedFlashList = useMemo(
    () => Animated.createAnimatedComponent(FlashList<ListItem>),
    []
  );

  /** FlashList + sticky tabs: spacer, MVP-off, post-frame scroll (TEST: parity on iOS + Android narrow). */
  const narrowMobileStickyListWorkaround = !isLargeScreen;

  const minContentHeight =
    flashListHeight - TABS_BAR_HEIGHT - (activeTab === 'photos' && !isEvent ? TABS_BAR_HEIGHT : 0);
  const footerSpacerHeight =
    narrowMobileStickyListWorkaround && isFooterSpacerActive ? flashListHeight : 0;

  // Determine photos per row based on screen size (only 2, 3, 4, or 6 columns)
  const photosPerRow = useMemo(() => {
    if (!isLargeScreen) return 3;
    if (width >= 1536) return 6;
    if (width >= 1024) return 4;
    if (width >= 768) return 3;
    return 3;
  }, [isLargeScreen, width]);

  const postsFlashListOptimizer = useFlashListOptimizer({
    estimatedRowsOnScreen: { mobile: 6, desktop: 7 },
  });
  const photosFlashListOptimizer = useFlashListOptimizer({
    estimatedRowsOnScreen: { mobile: 5, desktop: 4 },
    maxColsOnScreen: { mobile: 3, desktop: 6 },
  });

  const CenteredSection = ({ children }: { children: React.ReactNode }) => (
    <HStack className="flex-1 items-center justify-center" style={{ minHeight: minContentHeight }}>
      {children}
    </HStack>
  );

  const repeat = useCallback(
    (count: number, render: (index: number) => React.ReactNode) =>
      Array.from({ length: count }, (_, idx) => render(idx)),
    []
  );

  const activeTabConfig = useMemo(
    () => profileTabs.find((t) => t.key === activeTab),
    [profileTabs, activeTab]
  );
  const activeFlashListOptimizer =
    activeTabConfig?.queryType === 'photos' ? photosFlashListOptimizer : postsFlashListOptimizer;

  // Staff open every private folder on the website; the flag is separate from the member's grant
  // so the profile menu never offers them to forfeit a grant they never had. A locked folder is
  // never fetched or paged: the API would still return the photos to staff, and paging hidden
  // photos keeps the footer spinner blinking under "Private photos are locked".
  const canViewPrivateFolder =
    !!member?.IsPrivateFolderUnlocked || (Platform.OS === 'web' && !!member?.CanModerate);
  const isPrivateFolderLocked =
    activeTabConfig?.queryType === 'photos' && activeFolder === 'private' && !canViewPrivateFolder;

  const counts = useMemo(
    () => ({
      posts: member?.PostsCount ?? 0,
      photos: (member?.PublicPhotosCount ?? 0) + (member?.PrivatePhotosCount ?? 0),
      publicPhotos: member?.PublicPhotosCount ?? 0,
      privatePhotos: member?.PrivatePhotosCount ?? 0,
      events: member?.EventsCount ?? 0,
    }),
    [member?.PostsCount, member?.PublicPhotosCount, member?.PrivatePhotosCount, member?.EventsCount]
  );

  const postsQuery = useInfiniteQuery({
    queryKey: ['posts', 'wall', member?.Id],
    enabled: !!member?.Id && activeTabConfig?.queryType === 'posts',
    queryFn: async ({ pageParam }) =>
      MembersService.getMemberWall(
        member!.Id,
        buildPaginationParams(pageParam, postsFlashListOptimizer.pageSize)
      ),
    initialPageParam: undefined as PageParam,
    getNextPageParam: getNextPageSkipOrCursor,
    select: useCallback((data: InfiniteData<PostsView, PageParam>) => {
      const items = data.pages.flatMap((page) => page.PostItems);
      return { ...data, items } as InfiniteData<PostsView, PageParam> & { items: Post[] };
    }, []),
  });

  const photosQuery = useInfiniteQuery({
    queryKey: ['photos', 'member', member?.Id, activeFolder],
    enabled: !!member?.Id && activeTabConfig?.queryType === 'photos' && !isPrivateFolderLocked,
    queryFn: async ({ pageParam }) => {
      const method =
        activeFolder === 'public'
          ? MembersService.getMemberPublicPhotos
          : MembersService.getMemberPrivatePhotos;
      return method(
        member!.Id,
        buildPaginationParams(pageParam, photosFlashListOptimizer.pageSize)
      );
    },
    initialPageParam: undefined as PageParam,
    getNextPageParam: getNextPageSkipOrCursor,
    select: useCallback((data: InfiniteData<PhotosView, PageParam>) => {
      const items = data.pages.flatMap((page) => page.PhotosItems);
      const nonPremiumLimitReached = data.pages.some(
        (page) => page.NonPremiumLimitReached === true
      );
      return { ...data, items, nonPremiumLimitReached } as InfiniteData<PhotosView, PageParam> & {
        items: Photo[];
        nonPremiumLimitReached: boolean;
      };
    }, []),
  });
  const profileHasPhotosHiddenBySafeBrowsing =
    photosQuery.data?.pages?.[0]?.HasPhotosHiddenBySafeBrowsing ?? null;
  // First-page flag: show notice that this premium member's photos get a larger non-premium preview.
  const profileHasExtendedPhotoPreview =
    photosQuery.data?.pages?.[0]?.HasExtendedPhotoPreview === true;
  const showSensitivePhotosReminder =
    (Platform.OS === 'web' || Platform.OS === 'android') &&
    activeTab === 'photos' &&
    activeFolder === 'public' &&
    !isSelf &&
    !isEvent &&
    profileHasPhotosHiddenBySafeBrowsing === true &&
    isSensitiveReminderLoaded &&
    !isSensitiveReminderDismissed;

  const photosForCarousel =
    ((photosQuery.data as any)?.items as Photo[] | undefined) ?? EMPTY_PHOTOS;
  photosForCarouselRef.current = photosForCarousel;
  const getPhotosForCarousel = useCallback(() => photosForCarouselRef.current, []);

  // Tapping the profile photo opens the same preview carousel the About-tab row
  // uses, positioned on the profile photo (first item). We read the photos from
  // the same hook (and React Query cache), so both carousels are backed by one
  // shared array — no duplicate fetch. The profile photo is prepended (and
  // synthesized when it isn't part of the loaded public set, as happens for some
  // members) by the shared builder.
  const { data: previewPhotos } = useMemberPreviewPhotos(member?.Id, {
    enabled: (member?.PublicPhotosCount ?? 0) > 0,
  });
  const profilePhotoCarousel = useMemo(
    () => buildProfilePhotoCarousel(previewPhotos, member?.PhotoId, member?.PhotoUrl),
    [previewPhotos, member?.PhotoId, member?.PhotoUrl]
  );
  // Enable the carousel only when there is more than the profile photo to swipe;
  // otherwise the trigger keeps its single-photo open behavior.
  const profilePhotoCarouselRef = useRef<Photo[] | undefined>(undefined);
  profilePhotoCarouselRef.current =
    profilePhotoCarousel.length > 1 ? profilePhotoCarousel : undefined;
  const getProfilePhotoCarousel = useCallback(() => profilePhotoCarouselRef.current, []);

  const currentQuery = useMemo(() => {
    if (activeTabConfig?.queryType === 'posts') return postsQuery;
    if (activeTabConfig?.queryType === 'photos') return photosQuery;
    return null;
  }, [activeTabConfig?.queryType, postsQuery, photosQuery]);

  const data: ListItem[] = useMemo(() => {
    if (!member) return [];
    const items: ListItem[] = [];

    if (!isLargeScreen) {
      items.push({ type: 'parallaxImageSpacer' });
      items.push({ type: 'contentHeader' });
      items.push({ type: 'tabs' });
    }

    switch (activeTab) {
      case 'about':
        items.push({ type: 'tabComponent' });
        break;
      case 'events':
        items.push({ type: 'tabComponent' });
        break;
      case 'weight':
        items.push({ type: 'tabComponent' });
        break;
      case 'posts': {
        items.push({ type: 'spacer' });
        if (currentQuery?.error) {
          items.push({ type: 'listError' });
        } else if (currentQuery?.isPending) {
          items.push({ type: 'postsLoading' });
        } else if ((currentQuery?.data?.items?.length || 0) === 0) {
          items.push({ type: 'postsEmpty' });
        } else {
          const queryItems = ((currentQuery?.data as any)?.items as Post[] | undefined) ?? [];
          const list: ListItem[] = [];
          queryItems.forEach((post, index) => {
            list.push({ type: 'post', post, index });
            if (post.OverQuota !== QuotaTypes.None && (index - 4) % 5 === 0) {
              list.push({ type: 'postUpgrade', index });
            }
          });
          items.push(...list);
          if (!isLargeScreen && list.length < 4) {
            items.push({ type: 'spacer', className: 'h-[60vh]' });
          }
        }
        break;
      }
      case 'photos': {
        items.push({ type: 'spacer' });
        if (!isEvent) items.push({ type: 'photosFolderTabs' });
        if (showSensitivePhotosReminder) items.push({ type: 'sensitivePhotosReminder' });
        if (isPrivateFolderLocked) {
          items.push({ type: 'photosLocked' });
        } else if (currentQuery?.error) {
          items.push({ type: 'listError' });
        } else if (currentQuery?.isPending) {
          items.push({ type: 'photosLoading' });
        } else {
          if ((currentQuery?.data?.items?.length || 0) === 0)
            items.push({ type: 'photosEmpty' });
          else {
            if (profileHasExtendedPhotoPreview) {
              items.push({ type: 'photosExtraInfo' });
            }
            const queryItems = ((currentQuery?.data as any)?.items as Photo[] | undefined) ?? [];
            const rows: ListItem[] = [];
            for (let i = 0; i < queryItems.length; i += photosPerRow) {
              rows.push({ type: 'photoRow', photos: queryItems.slice(i, i + photosPerRow) });
            }
            items.push(...rows);
            // Show premium upgrade card when non-premium limit is reached
            if ((currentQuery?.data as any)?.nonPremiumLimitReached) {
              items.push({ type: 'photosUpgrade' });
            }
            if (!isLargeScreen && rows.length <= 4) {
              items.push({ type: 'spacer', className: 'h-[60vh]' });
            }
          }
        }
        break;
      }
      default:
        break;
    }

    items.push({ type: 'spacer', className: 'h-4' });

    // Events have no folder tabs bar (public/private), so compensate for the missing 54px on web mobile
    if (Platform.OS === 'web' && isEvent && activeTab === 'photos')
      items.push({ type: 'spacer', className: 'h-[54px]' });

    return items;
  }, [
    member,
    isLargeScreen,
    isEvent,
    activeTab,
    currentQuery?.error,
    currentQuery?.isPending,
    currentQuery?.data?.items,
    (currentQuery?.data as any)?.nonPremiumLimitReached,
    profileHasExtendedPhotoPreview,
    activeFolder,
    isPrivateFolderLocked,
    photosPerRow,
    showSensitivePhotosReminder,
  ]);

  const stickyHeaderIndices = useMemo(() => {
    if (isLargeScreen) return [] as number[];
    const idx = data.findIndex((i) => i.type === 'tabs');
    return idx >= 0 ? [idx] : [];
  }, [data, isLargeScreen]);

  const getMobileTabsThreshold = useCallback(() => {
    const parallaxHeight = member?.PhotoUrl ? Math.max(0, imageHeight - headerHeight) : 0;
    return Math.max(0, parallaxHeight + contentHeaderHeight - CONTENT_HEADER_OVERLAP);
  }, [member?.PhotoUrl, imageHeight, headerHeight, contentHeaderHeight]);

  const getMobileTabsOffset = useCallback(
    () => getMobileTabsThreshold() + STICKY_TABS_SCROLL_NUDGE,
    [getMobileTabsThreshold]
  );

  const getPhotosFolderTabsThreshold = useCallback(() => {
    if (isLargeScreen || isEvent || activeTab !== 'photos') return 0;
    return getMobileTabsThreshold() + TABS_BAR_HEIGHT + PHOTOS_FOLDER_TABS_UNDER_MAIN_GAP;
  }, [activeTab, getMobileTabsThreshold, isEvent, isLargeScreen]);

  const getPhotosFolderTabsOffset = useCallback(
    () => Math.max(0, getPhotosFolderTabsThreshold() - TABS_BAR_HEIGHT),
    [getPhotosFolderTabsThreshold]
  );

  const scrollToTabs = useCallback(
    (animated: boolean = false) => {
      if (!listRef.current) return;
      try {
        if (isLargeScreen) {
          listRef.current.scrollToOffset({ offset: 0, animated });
        } else {
          listRef.current.scrollToOffset({ offset: getMobileTabsOffset(), animated });
        }
      } catch {
        // no-op
      }
    },
    [isLargeScreen, getMobileTabsOffset]
  );

  const scrollToPhotosFolderTabs = useCallback(
    (animated: boolean = false) => {
      if (!listRef.current || isLargeScreen || isEvent) return;
      try {
        listRef.current.scrollToOffset({ offset: getPhotosFolderTabsOffset(), animated });
      } catch {
        // no-op
      }
    },
    [getPhotosFolderTabsOffset, isEvent, isLargeScreen]
  );

  const scrollToTabsIfNeeded = useCallback(
    (animated: boolean = true) => {
      if (!listRef.current) return;
      if (isLargeScreen && currentScrollY.value <= 0) return;
      scrollToTabs(animated);
    },
    [isLargeScreen, currentScrollY, scrollToTabs]
  );

  const scrollProfileToTop = useCallback(() => {
    if (!listRef.current || isLargeScreen) return;
    try {
      listRef.current.scrollToOffset({ offset: 0, animated: true });
    } catch {
      // no-op
    }
  }, [isLargeScreen]);

  const deactivateFooterSpacerSoon = useCallback(() => {
    if (footerSpacerTimeoutRef.current) {
      clearTimeout(footerSpacerTimeoutRef.current);
    }

    footerSpacerTimeoutRef.current = setTimeout(() => {
      footerSpacerTimeoutRef.current = null;
      setIsFooterSpacerActive(false);
    }, 250);
  }, []);

  const scheduleStickyListResync = useCallback(
    (options?: { resyncScroll?: () => void }) => {
      if (!narrowMobileStickyListWorkaround) return;
      requestAnimationFrame(() => {
        options?.resyncScroll?.();
        deactivateFooterSpacerSoon();
      });
    },
    [deactivateFooterSpacerSoon, narrowMobileStickyListWorkaround]
  );

  useEffect(
    () => () => {
      if (footerSpacerTimeoutRef.current) {
        clearTimeout(footerSpacerTimeoutRef.current);
      }
    },
    []
  );

  const handleTabChange = useCallback(
    (tab: ProfileTab) => {
      if (tab === activeTab) {
        scrollToTabsIfNeeded(true);
        return;
      }
      const tabConfig = profileTabs.find((t) => t.key === tab);
      if (tabConfig?.queryType === 'posts' && member) {
        const queryKey = ['posts', 'wall', member.Id];
        queryClient.removeQueries({ queryKey });
      }
      const shouldShowTabStart = isLargeScreen ? currentScrollY.value > 0 : true;
      if (shouldShowTabStart && narrowMobileStickyListWorkaround) {
        setIsFooterSpacerActive(true);
      }
      setActiveTab(tab);
      if (shouldShowTabStart) {
        const tabsThresholdY = getMobileTabsThreshold();
        const animateScrollToTabs = isLargeScreen ? false : currentScrollY.value < tabsThresholdY;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollToTabs(animateScrollToTabs);
            if (narrowMobileStickyListWorkaround) {
              scheduleStickyListResync({});
            }
          });
        });
      }
      router.setParams(tab === 'about' ? { tab: undefined } : { tab });
    },
    [
      activeTab,
      currentScrollY,
      scheduleStickyListResync,
      narrowMobileStickyListWorkaround,
      isLargeScreen,
      member,
      queryClient,
      profileTabs,
      scrollToTabs,
      scrollToTabsIfNeeded,
      getMobileTabsThreshold,
    ]
  );
  handleTabChangeRef.current = handleTabChange;

  const handlePhotosFolderChange = useCallback(
    (folder: 'public' | 'private') => {
      if (folder === activeFolder) return;
      const shouldShowFolderTabStart =
        narrowMobileStickyListWorkaround &&
        activeTab === 'photos' &&
        currentScrollY.value >= getMobileTabsThreshold() - 1;
      const shouldScrollToFolderTabs =
        shouldShowFolderTabStart && currentScrollY.value >= getPhotosFolderTabsThreshold() - 1;

      if (shouldShowFolderTabStart) {
        setIsFooterSpacerActive(true);
      }

      setActiveFolder(folder);

      if (shouldShowFolderTabStart) {
        scheduleStickyListResync({
          resyncScroll: shouldScrollToFolderTabs
            ? () => scrollToPhotosFolderTabs(false)
            : undefined,
        });
      }
    },
    [
      activeFolder,
      activeTab,
      currentScrollY,
      scheduleStickyListResync,
      narrowMobileStickyListWorkaround,
      getMobileTabsThreshold,
      getPhotosFolderTabsThreshold,
      scrollToPhotosFolderTabs,
    ]
  );

  const handleEndReached = useCallback(() => {
    if (!currentQuery || isPrivateFolderLocked) return;
    if (currentQuery.hasNextPage && !currentQuery.isFetchingNextPage) currentQuery.fetchNextPage();
  }, [currentQuery, isPrivateFolderLocked]);

  const confirmDroppedUpload = useCallback(
    async (file: File) => {
      let closeLoading: (() => void) | undefined;
      try {
        setIsUploadingPhoto(true);
        closeLoading = showLoadingToast(t('photos.uploadingImage', 'Uploading image...'));
        await PhotosService.uploadPhoto(file, {
          eventId: isEventCreator ? member?.Id : undefined,
          isPrivate: activeFolder === 'private',
          caption: pendingCaption?.trim() ? pendingCaption.trim() : undefined,
        });
        setPendingCaption('');
        await photosQuery.refetch();
        showSuccessToast(t('photos.uploadSuccess', 'Photo uploaded'), undefined, 'bottom');
      } catch (e) {
        showErrorToast(t('photos.uploadFailed', 'Failed to upload photo'), e as any);
      } finally {
        try {
          closeLoading?.();
        } catch {}
        setIsUploadingPhoto(false);
      }
    },
    [
      activeFolder,
      isEventCreator,
      member?.Id,
      pendingCaption,
      photosQuery,
      t,
      showLoadingToast,
      showSuccessToast,
      showErrorToast,
    ]
  );

  const tabsScrollXRef = useRef(0);

  const renderTabSuffix = useCallback(
    (tab: TabItem) => {
      const cfg = tab as ProfileTabConfig;
      const count =
        cfg.queryType === 'posts'
          ? counts.posts
          : cfg.queryType === 'photos'
            ? counts.photos
            : cfg.key === 'events'
              ? counts.events
              : undefined;
      return typeof count === 'number' ? (
        <CountBadge
          count={count}
          size="xs"
          className={
            isLargeScreen
              ? 'mr-0 bg-background-100 dark:bg-background-200'
              : 'mr-0 bg-background-200 dark:bg-background-200'
          }
          textClassName="text-typography-600 dark:text-typography-700 font-semibold"
        />
      ) : null;
    },
    [counts.posts, counts.photos, counts.events, isLargeScreen]
  );

  const renderProfileTabsBar = useCallback(
    () => (
      <InlineTabsBar
        tabs={profileTabs}
        activeTab={activeTab}
        onTabChange={(tab) => handleTabChange(tab as ProfileTab)}
        scrollXRef={tabsScrollXRef}
        renderSuffix={renderTabSuffix}
        className="bg-background-50 dark:bg-background-0"
      />
    ),
    [activeTab, handleTabChange, profileTabs, renderTabSuffix]
  );

  const [isUserRefreshing, setIsUserRefreshing] = useState(false);
  const onRefresh = useCallback(async () => {
    setIsUserRefreshing(true);
    try {
      // Always refetch member details to update permission state
      await refetch();
      // Also refetch the active tab's query if there is one (refetch ignores `enabled`, so the
      // locked private folder is skipped explicitly)
      if (currentQuery && !isPrivateFolderLocked) await currentQuery.refetch();
    } finally {
      setIsUserRefreshing(false);
    }
  }, [currentQuery, refetch, isPrivateFolderLocked]);

  const renderListFooter = useCallback(() => {
    const loadingMore = !!currentQuery?.isFetchingNextPage;
    return (
      <>
        {loadingMore ? (
          <Box className="mx-4 mb-6 mt-0">
            <ActivityIndicator color="grey" />
          </Box>
        ) : null}
        {footerSpacerHeight > 0 ? <View style={{ height: footerSpacerHeight }} /> : null}
      </>
    );
  }, [currentQuery?.isFetchingNextPage, footerSpacerHeight]);

  // Track the image-viewer overlay so the close effect below can re-sync the parallax offset.
  // The list is intentionally NOT frozen while the viewer is open (react-freeze flashed a blank
  // sheet over the profile before the viewer's open animation covered it); the Android
  // shadow-tree measure race it guarded against (GROKIO-APP-145/170/16T) is now fixed natively
  // via the RN 0.86 feature flag enabled in plugins/withShadowTreeRaceFix.js.
  const { isVisible: isViewerOpen } = useImageViewerState();

  const scrollHandler = useAnimatedScrollHandler(
    {
      onScroll: (event) => {
        const y = event.contentOffset.y;
        currentScrollY.value = y;
        if (!isLargeScreen) {
          updateScrollY(y);
        }
      },
    },
    [isLargeScreen, updateScrollY, currentScrollY]
  );

  // Whenever the list resumes without scroll events — image-viewer close, returning to the
  // screen (the stacks set freezeOnBlur), or app foreground — a re-measure can leave the
  // parallax photo / background overlay offset from the list's real position (photo appears cut
  // off below the header, blank space below). Re-assert the resting offset so the offset-driven
  // styles re-sync without a user scroll.
  const resyncRestingOffset = useCallback(() => {
    if (isLargeScreen || Platform.OS === 'web') return;
    const id = requestAnimationFrame(() => {
      listRef.current?.scrollToOffset({ offset: currentScrollY.value, animated: false });
    });
    return () => cancelAnimationFrame(id);
  }, [isLargeScreen, currentScrollY]);

  const prevViewerOpenRef = useRef(isViewerOpen);
  useEffect(() => {
    const justClosed = prevViewerOpenRef.current && !isViewerOpen;
    prevViewerOpenRef.current = isViewerOpen;
    if (!justClosed) return;
    return resyncRestingOffset();
  }, [isViewerOpen, resyncRestingOffset]);

  // Skip the initial focus on mount — the list is at rest and a queued scrollToOffset(0) could
  // fight the direct-to-tab landing scroll.
  const hasFocusedOnceRef = useRef(false);
  useFocusEffect(
    useCallback(() => {
      if (!hasFocusedOnceRef.current) {
        hasFocusedOnceRef.current = true;
        return;
      }
      return resyncRestingOffset();
    }, [resyncRestingOffset])
  );

  useAppState({ onForeground: resyncRestingOffset });

  const keyExtractor = useCallback(
    (item: ListItem, index: number) => {
      switch (item.type) {
        case 'post':
          return `post-${item.post.Id}`;
        case 'photoRow':
          return `photoRow-${item.photos.map((p) => p.Id).join('-')}`;
        case 'postUpgrade':
          return `postUpgrade-${item.index}`;
        case 'tabComponent':
          return `tabComponent-${activeTab}`;
        default:
          return `${item.type}-${index}`;
      }
    },
    [activeTab]
  );

  const getItemType = useCallback((item: ListItem) => item.type, []);

  const listExtraData = useMemo(
    () => ({
      activeFolder,
      activeTab,
      counts,
      photosPerRow,
      profileTabs,
      // imageHeight/headerHeight drive the parallaxImageSpacer cell height; include them so
      // the spacer re-renders when the async image size resolves (e.g. on slow connections)
      // instead of staying stale until the next scroll.
      imageHeight,
      headerHeight,
    }),
    [activeFolder, activeTab, counts, photosPerRow, profileTabs, imageHeight, headerHeight]
  );

  const onContentHeaderOuterLayout = useCallback(
    (e: { nativeEvent: { layout: { height: number } } }) => {
      const h = e.nativeEvent.layout.height;
      if (h > 0) setContentHeaderHeight(h);
    },
    []
  );

  const onContentHeaderInnerLayout = useCallback(
    (e: { nativeEvent: { layout: { height: number } } }) => {
      const h = e.nativeEvent.layout.height;
      if (h > 0) {
        setContentHeaderHeight((prev) => (prev > 0 ? prev : h));
      }
    },
    []
  );

  useLayoutEffect(() => {
    if (Platform.OS !== 'web' || isLargeScreen || contentHeaderHeight > 0) return;
    const node = contentHeaderContainerRef.current;
    if (!node) return;
    let cancelled = false;
    let id2: number | undefined;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => {
        if (cancelled) return;
        node.measure?.((_x, _y, _w, h) => {
          if (h > 0) setContentHeaderHeight((prev) => (prev > 0 ? prev : h));
        });
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id1);
      if (id2 !== undefined) cancelAnimationFrame(id2);
    };
  }, [contentHeaderHeight, imageHeight, isLargeScreen, member?.Id, activeTab]);

  const renderItem: ListRenderItem<ListItem> = useCallback(
    ({ item }: { item: ListItem }) => {
      if (!member) return null;

      const itemRenderers: Record<string, (i: any) => React.ReactNode> = {
        parallaxImageSpacer: () => (
          <ImageViewerTrigger
            uri={member.PhotoUrl || ''}
            photoId={member.PhotoId ?? undefined}
            getPhotos={getProfilePhotoCarousel}
            disabled={!member.PhotoId}
            allowImageDownload={!!isEvent}>
            <View style={{ height: Math.max(0, imageHeight - headerHeight) }} />
          </ImageViewerTrigger>
        ),
        spacer: (i: { className?: string }) => <Box className={i.className ?? 'h-2 md:h-4'} />,
        contentHeader: () => (
          <View
            ref={contentHeaderContainerRef}
            className="-mt-5 rounded-t-3xl bg-background-50 dark:bg-background-0"
            style={Platform.OS === 'web' ? { alignSelf: 'stretch', width: '100%' } : undefined}
            onLayout={onContentHeaderOuterLayout}>
            <VStack className="gap-3 px-4 py-5" onLayout={onContentHeaderInnerLayout}>
              {isEvent && eventDetails ? (
                <>
                  <EventActions member={member} />
                  <EventOverview event={eventDetails} />
                </>
              ) : (
                <>
                  <ProfileActions member={member} />
                  <ProfileOverview member={member} title={member.UserName || userName} />
                </>
              )}
            </VStack>
          </View>
        ),
        tabs: () => renderProfileTabsBar(),
        tabComponent: () => {
          const TabComponent = activeTabConfig?.component;
          if (!TabComponent) return null;
          return (
            <View className="py-3" style={{ minHeight: minContentHeight }}>
              <TabComponent member={member} />
            </View>
          );
        },
        postsLoading: () => (
          <VStack style={{ minHeight: minContentHeight }}>
            {repeat(5, (index) => (
              <CardWrapper key={`post-skeleton-${index}`}>
                <PostCard />
              </CardWrapper>
            ))}
          </VStack>
        ),
        listError: () => (
          <CenteredSection>
            <ErrorInListView
              error={currentQuery?.error as any}
              onRetry={() => currentQuery?.refetch()}
              isRetrying={!!currentQuery?.isRefetching}
            />
          </CenteredSection>
        ),
        postsEmpty: () => (
          <CenteredSection>
            <MessageView
              type="info"
              text={t('profile.wall.empty', 'No posts to show')}
              icon={AlbumNotFound02Icon}
            />
          </CenteredSection>
        ),
        post: (i: { post: Post }) => (
          <CardWrapper>
            <PostCard post={i.post} onPostDeleted={handlePostDeleted} />
          </CardWrapper>
        ),
        postUpgrade: () => (
          <CardWrapper>
            <PostCardUpgrade />
          </CardWrapper>
        ),
        photosFolderTabs: () => (
          <InlineTabsBar
            tabs={[
              { key: 'public', label: t('profile.photos.public', 'Public') },
              { key: 'private', label: t('profile.photos.private', 'Private') },
            ]}
            activeTab={activeFolder}
            onTabChange={(tab) => handlePhotosFolderChange(tab as 'public' | 'private')}
            variant="buttons"
            className="border-none bg-transparent"
            renderSuffix={(tab) => {
              const count = tab.key === 'public' ? counts.publicPhotos : counts.privatePhotos;
              return typeof count === 'number' ? (
                <CountBadge
                  count={count}
                  size="xs"
                  className="mr-0 bg-black/40 py-0.5"
                  textClassName="text-white dark:text-typography-800 font-semibold"
                />
              ) : null;
            }}
          />
        ),
        sensitivePhotosReminder: () => (
          <Box className="mx-2 mt-3 md:mx-4">
            <SensitivePhotosReminderCard
              onOpenSettings={() => setIsSafetySheetOpen(true)}
              onDismiss={dismissSensitiveReminder}
            />
          </Box>
        ),
        photosLoading: () => (
          <VStack className="gap-3 px-2 py-2 md:px-4" style={{ minHeight: minContentHeight }}>
            {repeat(8, (row) => (
              <HStack key={`photo-skeleton-row-${row}`} className="gap-3">
                {repeat(photosPerRow, (col) => (
                  <View key={`photo-skeleton-${row}-${col}`} className="flex-1">
                    <PhotoCard />
                  </View>
                ))}
              </HStack>
            ))}
          </VStack>
        ),
        photosEmpty: () => (
          <CenteredSection>
            <MessageView type="info" text={t('photos.empty')} icon={AlbumNotFound02Icon} />
          </CenteredSection>
        ),
        photosLocked: () => {
          const privatePending = member.IsUnlockRequestPending;
          const hasPrivatePhotos = (member.PrivatePhotosCount || 0) > 0;
          const handleRequestUnlock = async () => {
            try {
              await MembersService.requestPhotoUnlock(member.Id);
              showSuccessToast(t('profile.photos.requestSubmitted', 'Access request sent'));
              updateSingleQuery(queryKey, {
                IsUnlockRequestPending: true,
              });
            } catch (e) {
              showErrorToast(t('profile.photos.requestFailed', 'Failed to send access request'), e);
            }
          };
          return (
            <CenteredSection>
              <MessageView
                type="info"
                text={
                  privatePending
                    ? t(
                        'profile.photos.requestPending',
                        'Your request to access private photos is pending approval'
                      )
                    : !hasPrivatePhotos
                      ? t('photos.empty')
                      : t('profile.photos.privateLocked', 'Private photos are locked')
                }
                icon={!hasPrivatePhotos ? AlbumNotFound02Icon : FolderLockedIcon}>
                {!privatePending && hasPrivatePhotos && (
                  <HStack className="mt-2">
                    <ButtonWrapper
                      text={t('profile.photos.requestAccess', 'Request access')}
                      size="md"
                      onPress={handleRequestUnlock}
                    />
                  </HStack>
                )}
              </MessageView>
            </CenteredSection>
          );
        },
        photoRow: (i: { photos: Photo[] }) => (
          <HStack className="mt-3 gap-3 px-2 md:px-4">
            {i.photos.map((p) => (
              <View key={`photo-${p.Id}`} className="flex-1">
                <PhotoCard
                  photo={p}
                  showLikesComments={true}
                  getPhotosForCarousel={getPhotosForCarousel}
                />
              </View>
            ))}
            {i.photos.length < photosPerRow &&
              Array.from({ length: photosPerRow - i.photos.length }).map((_, idx) => (
                <View key={`spacer-${idx}`} className="flex-1" />
              ))}
          </HStack>
        ),
        photosUpgrade: () => (
          <Box className="mx-2 my-4 md:mx-4">
            <PremiumUpgradeCard listType="photos" />
          </Box>
        ),
        photosExtraInfo: () => (
          <View className="mx-2 mt-3 mb-1 px-2 md:mx-4 md:px-4">
            <Text size="sm" className="text-typography-500">
              {t('profile.photos.extendedPreviewNotice', {
                premiumPlan: community?.premiumPlan ?? 'Premium',
              })}
            </Text>
          </View>
        ),
      };

      const renderer = itemRenderers[item.type as keyof typeof itemRenderers];
      return renderer ? (renderer(item as any) as unknown as React.ReactElement) : null;
    },
    [
      member,
      imageHeight,
      headerHeight,
      activeTabConfig?.component,
      flashListHeight,
      userName,
      currentQuery?.error,
      currentQuery?.isRefetching,
      t,
      community?.premiumPlan,
      activeFolder,
      activeTab,
      counts.privatePhotos,
      counts.publicPhotos,
      getPhotosForCarousel,
      getProfilePhotoCarousel,
      handlePhotosFolderChange,
      showSuccessToast,
      renderProfileTabsBar,
      onContentHeaderInnerLayout,
      onContentHeaderOuterLayout,
      updateSingleQuery,
      showErrorToast,
      photosPerRow,
      minContentHeight,
      isEvent,
      dismissSensitiveReminder,
    ]
  );

  const isProfileLoading =
    isLoading ||
    (!isLargeScreen && !!member?.PhotoUrl && imageHeight === 0) ||
    (isLargeScreen && !!member?.PhotoUrl && !largeHeaderImageLoaded);
  const largeHeaderProfilePhotoSize = isEvent ? 208 : 128;

  return (
    <View className="flex-1">
      {isLargeScreen ? (
        <ScreenHeader
          options={{
            // The header back button sits over the cover photo — tell the title
            // bar up front so it uses the blur-pill style from first render,
            // before the cover image loads (avoids a plain→blur chevron flip).
            backButtonOnImage: true,
            title: isEvent && eventDetails ? eventDetails.Name : member?.UserName || userName,
            headerTitle: () => null,
            headerBackgroundContent: member?.PhotoUrl ? (
              // Kept mounted so the image loads (onLoadEnd drives the spinner),
              // but invisible until loaded so the blurred cover doesn't show
              // behind a lone back button — it reveals with the header.
              <View
                className="absolute inset-0"
                pointerEvents="none"
                style={{ opacity: largeHeaderImageLoaded ? 1 : 0 }}>
                <BlurTargetView
                  ref={largeHeaderBlurTargetRef}
                  style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: '100%' }}>
                  <Image
                    source={{ uri: member.PhotoUrl }}
                    className="absolute inset-0 h-full w-full"
                    contentFit="cover"
                    onLoadEnd={() => setLargeHeaderImageLoaded(true)}
                  />
                  {Platform.OS !== 'web' && effectiveColorScheme === 'light' && (
                    <View className="absolute inset-0 bg-white/40" />
                  )}
                </BlurTargetView>
                <BlurView
                  blurTarget={largeHeaderBlurTargetRef}
                  intensity={80}
                  tint={effectiveColorScheme}
                  className="absolute inset-0"
                />
              </View>
            ) : undefined,
            // Gated on !isProfileLoading so the header doesn't pop into the title
            // bar mid-load (which would grow the bar and shove the centered
            // spinner down). Header + content reveal together once ready.
            headerLeft: () =>
              member && !isProfileLoading ? (
                <HStack className={`flex-1 items-start gap-5 pb-2 ${isWideHeader ? 'pr-4' : ''}`}>
                  <ImageViewerTrigger
                    uri={member.PhotoUrl || ''}
                    photoId={member.PhotoId ?? undefined}
                    getPhotos={getProfilePhotoCarousel}
                    disabled={!member.PhotoId}
                    allowImageDownload={!!isEvent}>
                    <ProfilePicture
                      member={member}
                      size="custom"
                      showPresence={false}
                      className="mt-1 flex-shrink-0"
                      style={{
                        height: largeHeaderProfilePhotoSize,
                        width: largeHeaderProfilePhotoSize,
                      }}
                      imageClassName="rounded-xl"
                      disableNavigation={true}
                    />
                  </ImageViewerTrigger>
                  {!isWideHeader ? (
                    <VStack className="min-w-0 flex-1 gap-3">
                      <HStack className="flex-row flex-wrap items-center gap-3">
                        {isEvent && eventDetails ? (
                          <>
                            <EventActions member={member} className="flex-1" />
                            <EventContextMenu member={member} />
                          </>
                        ) : (
                          <>
                            <ProfileActions member={member} className="flex-1" />
                            {!isSelf && !member.IsAdmin && <ProfileContextMenu member={member} />}
                          </>
                        )}
                      </HStack>
                      {isEvent && eventDetails ? (
                        <EventOverview event={eventDetails} />
                      ) : (
                        <ProfileOverview member={member} title={member.UserName || userName} />
                      )}
                    </VStack>
                  ) : isEvent && eventDetails ? (
                    <EventOverview event={eventDetails} />
                  ) : (
                    <ProfileOverview member={member} title={member.UserName || userName} />
                  )}
                </HStack>
              ) : null,
            headerRight: () =>
              isWideHeader && member && !isProfileLoading ? (
                <HStack className="mt-2 flex-row flex-wrap justify-end gap-3">
                  {isEvent && eventDetails ? (
                    <>
                      <EventActions member={member} className="justify-end" />
                      <EventContextMenu member={member} />
                    </>
                  ) : (
                    <>
                      <ProfileActions member={member} className="justify-end" />
                      {!isSelf && !member.IsAdmin && <ProfileContextMenu member={member} />}
                    </>
                  )}
                </HStack>
              ) : null,
            inlineTabsBar: member && !isProfileLoading ? (
              <InlineTabsBar
                tabs={profileTabs}
                activeTab={activeTab}
                onTabChange={(tab) => handleTabChange(tab as ProfileTab)}
                renderSuffix={renderTabSuffix}
                className="border-none bg-background-50/50 dark:border-none dark:bg-black/50"
                textClassName="text-typography-600 dark:text-typography-500"
              />
            ) : null,
          }}
        />
      ) : (
        <>
          {member?.PhotoUrl && imageHeight > 0 ? (
            <ImageViewerTrigger
              uri={member.PhotoUrl || ''}
              photoId={member.PhotoId ?? undefined}
              getPhotos={getProfilePhotoCarousel}
              disabled={!member.PhotoId}
              allowImageDownload={!!isEvent}>
              <Animated.View
                style={[
                  {
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: imageHeight,
                    zIndex: -1,
                  },
                  backgroundImageStyle,
                ]}>
                <View
                  style={[
                    { position: 'absolute', left: 0, top: 0, height: '100%', width: '100%' },
                    Platform.OS === 'android' ? ANDROID_CSS_BLUR_CLIP_STYLE : undefined,
                  ]}>
                  <View className="absolute inset-0 bg-background-50" />
                  <Image
                    source={{ uri: member.PhotoUrl }}
                    style={{ minHeight: headerHeight, height: '100%', width: '100%' }}
                  />
                  <Animated.View
                    pointerEvents="none"
                    style={[
                      { position: 'absolute', left: 0, top: 0, height: '100%', width: '100%' },
                      blurredImageOpacityStyle,
                    ]}>
                    <BlurredExpoImage
                      source={{ uri: member.PhotoUrl }}
                      blur={PROFILE_SCROLL_BLUR_CONFIG}
                      style={{ minHeight: headerHeight, height: '100%', width: '100%' }}
                    />
                  </Animated.View>
                  <Animated.View
                    pointerEvents="none"
                    style={[
                      {
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: '100%',
                        backgroundColor: 'black',
                      },
                      darkenOverlayStyle,
                    ]}
                  />
                </View>
              </Animated.View>
            </ImageViewerTrigger>
          ) : null}

          <ScreenHeader
            options={{
              title: member?.UserName || userName,
              headerTitle: ({ children }) => (
                <Pressable
                  onPress={scrollProfileToTop}
                  className="flex-1"
                  accessibilityRole="button"
                  accessibilityLabel={t('common.scrollToTop', 'Scroll to top')}>
                  <Text size="lg" bold className="text-white opacity-0" numberOfLines={1}>
                    {children}
                  </Text>
                </Pressable>
              ),
              headerTransparent: true,
              headerRight: () =>
                member && isEvent ? (
                  <EventContextMenu member={member} />
                ) : member && !isSelf && !member.IsAdmin ? (
                  <ProfileContextMenu member={member} />
                ) : null,
            }}
          />

          <Animated.View
            style={[{ position: 'absolute', left: 0, right: 0, top: insetsTop }, fixedHeaderStyle]}>
            {member && <ProfileMobileHeader member={member} title={member.UserName || userName} />}
          </Animated.View>
        </>
      )}

      {isProfileLoading ? (
        <Box className="flex-1 items-center justify-center">
          <ActivityIndicator size="large" color="grey" />
        </Box>
      ) : error ? (
        <ErrorInListView error={error} onRetry={() => refetch()} isRetrying={isRefetching} />
      ) : !member ? (
        <MessageView type="warning" text={t('member.notFound')} icon={Alert02Icon} />
      ) : (
        <View className="relative flex-1">
          {/* Background view that covers the area below the photo */}
          {!isLargeScreen && member?.PhotoUrl && (
            <Animated.View
              style={[
                { position: 'absolute', left: 0, right: 0, height: height * 2, zIndex: 1 },
                contentBackgroundStyle,
              ]}>
              <View className="h-full w-full bg-background-50 dark:bg-background-0" />
            </Animated.View>
          )}

          <AnimatedFlashList
            ref={listRef}
            extraData={listExtraData}
            data={data}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            getItemType={getItemType}
            style={{
              marginTop: isLargeScreen ? 0 : headerHeight,
              zIndex: 2,
              backgroundColor: 'transparent',
            }}
            onLayout={handleFlashListLayout}
            onScroll={scrollHandler}
            // Fewer scroll samples on Android — parallax/blur worklets drive Fabric sync mounts; throttling reduces ANR risk (GROKIO-APP-PF/MR/MD).
            scrollEventThrottle={Platform.OS === 'android' ? 32 : 16}
            maintainVisibleContentPosition={
              narrowMobileStickyListWorkaround
                ? { disabled: true }
                : { autoscrollToTopThreshold: 10 }
            }
            stickyHeaderIndices={stickyHeaderIndices}
            onEndReached={handleEndReached}
            onEndReachedThreshold={activeFlashListOptimizer.onEndReachedThreshold}
            ListFooterComponent={renderListFooter}
            refreshControl={<RefreshControl refreshing={isUserRefreshing} onRefresh={onRefresh} />}
          />

          {/* Web-only drag-and-drop upload when viewing own Photos tab */}
          {Platform.OS === 'web' && activeTab === 'photos' && (isSelf || isEventCreator) ? (
            <WebDropImageUpload
              isEnabled
              overlayLabel={
                activeFolder === 'private'
                  ? t('photos.dropToUploadPrivate', 'Drop to upload to Private')
                  : t('photos.dropToUploadPublic', 'Drop to upload to Public')
              }
              title={
                activeFolder === 'private'
                  ? t('photos.addPrivatePhoto', 'Add a Private Photo')
                  : t('photos.addPublicPhoto', 'Add a Public Photo')
              }
              caption={pendingCaption}
              onCaptionChange={setPendingCaption}
              isUploading={isUploadingPhoto}
              onUpload={confirmDroppedUpload}
              showPolicyNotice
            />
          ) : null}

          {activeTab === 'posts' && member && !member.IsAdmin ? (
            <Fab
              size="lg"
              className="bg-primary-500 hover:bg-primary-700 active:bg-primary-800"
              onPress={() => setIsAddPostSheetOpen(true)}>
              <HugeIconsWrapper icon={QuillWrite02Icon} className="text-white" />
            </Fab>
          ) : null}

          {activeTab === 'photos' && (isSelf || isEventCreator) ? (
            <ImageUploadButton
              mode="preview"
              placement="top right"
              isUploading={isUploadingPhoto}
              caption={pendingCaption}
              onCaptionChange={setPendingCaption}
              showPolicyNotice={activeFolder !== 'private'}
              previewTitle={
                activeFolder === 'private'
                  ? t('photos.addPrivatePhoto', 'Add a Private Photo')
                  : t('photos.addPublicPhoto', 'Add a Public Photo')
              }
              onImageReady={async (image, opts) => {
                let closeLoading: (() => void) | undefined;
                try {
                  setIsUploadingPhoto(true);
                  closeLoading = showLoadingToast(t('photos.uploadingImage', 'Uploading image...'));
                  await PhotosService.uploadPhoto(image, {
                    eventId: isEventCreator ? member!.Id : undefined,
                    isPrivate: activeFolder === 'private',
                    caption: opts?.caption?.trim() ? opts?.caption?.trim() : undefined,
                  });
                  setPendingCaption('');
                  await photosQuery.refetch();
                  showSuccessToast(
                    t('photos.uploadSuccess', 'Photo uploaded'),
                    undefined,
                    'bottom'
                  );
                  if (member?.UserName) {
                    const countField =
                      activeFolder === 'private' ? 'PrivatePhotosCount' : 'PublicPhotosCount';
                    queryClient.setQueryData(queryKey, (oldData: MemberDetails | undefined) => {
                      if (!oldData) return oldData;
                      const currentCount = oldData[countField] ?? 0;
                      return plainToClass(MemberDetails, {
                        ...oldData,
                        [countField]: currentCount + 1,
                      });
                    });
                  }
                } catch (e) {
                  showErrorToast(t('photos.uploadFailed', 'Failed to upload photo'), e as any);
                } finally {
                  try {
                    /* close loading toast if shown */ closeLoading?.();
                  } catch {}
                  setIsUploadingPhoto(false);
                }
              }}
              renderTrigger={(triggerProps) => (
                <Fab
                  {...triggerProps}
                  size="lg"
                  placement={Platform.OS === 'web' ? 'bottom right' : 'attached'}
                  className="bg-primary-500 hover:bg-primary-700 active:bg-primary-800"
                  style={Platform.OS !== 'web' ? { position: 'relative' } : undefined}
                  disabled={isUploadingPhoto}>
                  <HugeIconsWrapper icon={Add01Icon} className="text-white" />
                </Fab>
              )}
            />
          ) : null}
        </View>
      )}
      <ContextSheet
        isOpen={isAddPostSheetOpen}
        onClose={() => setIsAddPostSheetOpen(false)}
        contentComponent={AddPost}
        title={t('post.newPost')}
        payload={
          isSelf
            ? {
                target: 'feed',
                onPostCreated: () => {
                  setIsAddPostSheetOpen(false);
                  setTimeout(() => {
                    postsQuery.refetch();
                    refetch();
                  }, 300);
                },
              }
            : isEventCreator
              ? {
                  target: 'event',
                  member,
                  onPostCreated: () => {
                    setIsAddPostSheetOpen(false);
                    setTimeout(() => {
                      postsQuery.refetch();
                      refetch();
                    }, 300);
                  },
                }
              : {
                  target: 'wall',
                  member,
                  onPostCreated: () => {
                    setIsAddPostSheetOpen(false);
                    setTimeout(() => {
                      postsQuery.refetch();
                    }, 300);
                  },
                }
        }
      />
      <ContextSheet
        isOpen={isSafetySheetOpen}
        onClose={() => {
          setIsSafetySheetOpen(false);
          photosQuery.refetch();
        }}
        contentComponent={SafetySettingsContent}
        title={t('settings.safety.title')}
      />
    </View>
  );
}
